import React from "react";

import { classifyStatusCell, type StatusTone } from "../utils/statusPill";

/**
 * Tailwind classes per tone. Slash-opacity, not `rgba(...)` arbitrary values:
 * Tailwind's extractor does not emit a class whose arbitrary value contains
 * commas, so the rgba form silently produced no rule at all in the production
 * bundle.
 *
 * `teal` is the one solid fill — it marks the covenant-basis row the mockup
 * highlights, which reads as a label rather than a judgement.
 */
const TONE_CLASSES: Record<StatusTone, string> = {
	red: "bg-[#fee2e2] border-[#fca5a5] text-[#991b1b] dark:bg-[#991b1b]/25 dark:border-[#fca5a5]/40 dark:text-[#fca5a5]",
	amber:
		"bg-[#fef3c7] border-[#fcd34d] text-[#92400e] dark:bg-[#92400e]/25 dark:border-[#fcd34d]/40 dark:text-[#fcd34d]",
	green:
		"bg-[#004C4B]/10 border-[#004C4B]/25 text-[#004C4B] dark:bg-[#004C4B]/40 dark:border-[#5eead4]/30 dark:text-[#5eead4]",
	teal: "bg-[#004C4B] border-[#004C4B] text-white dark:bg-[#5eead4] dark:border-[#5eead4] dark:text-[#00302f]",
	grey: "bg-[#f3f4f6] border-[#e5e7eb] text-[#4b5563] dark:bg-[#4b5563]/30 dark:border-[#9ca3af]/30 dark:text-[#d1d5db]",
};

interface StatusPillProps {
	tone: StatusTone;
	label: React.ReactNode;
}

/** Shape matches the cabinet StatusChip so the two read as one system. */
const StatusPill: React.FC<StatusPillProps> = ({ tone, label }) => (
	<span
		className={`inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-[12.5px] font-semibold leading-tight ${TONE_CLASSES[tone]}`}
	>
		{label}
	</span>
);

export default StatusPill;

/**
 * Flatten a table cell's children to the text the classifier reads, or `null`
 * when the cell holds markup a pill would destroy.
 *
 * Emphasis is decoration — answers write `**in** — uncapped` — so `<strong>`
 * and `<em>` wrappers are flattened to their text. Anything else (a citation
 * anchor, a link) carries an affordance the pill cannot replace, so such a
 * cell never becomes one.
 */
export function cellPillText(children: React.ReactNode): string | null {
	if (children === null || children === undefined || children === false) {
		return "";
	}
	if (typeof children === "string") return children;
	if (typeof children === "number") return String(children);
	if (Array.isArray(children)) {
		const parts = children.map(cellPillText);
		return parts.some((p) => p === null) ? null : parts.join("");
	}
	if (React.isValidElement(children)) {
		if (children.type !== "strong" && children.type !== "em") return null;
		return cellPillText(
			(children.props as { children?: React.ReactNode }).children,
		);
	}
	return null;
}

/**
 * A table cell whose LEADING token is one of the status / treatment terms
 * renders that token as a pill, with the reason that follows it beside the
 * pill as plain text — `Breach — FCCR by 0.29x` becomes a red `Breach` pill
 * and `FCCR by 0.29x`. Anything else, including a cell holding a citation
 * anchor, stays exactly as the markdown produced it.
 */
export const StatusCell: React.FC<
	React.TdHTMLAttributes<HTMLTableCellElement> & { node?: unknown }
> = (props) => {
	const { node: _node, children, ...rest } = props;
	const match = classifyStatusCell(cellPillText(children));
	if (!match) {
		return <td {...rest}>{children}</td>;
	}
	return (
		<td {...rest}>
			<StatusPill tone={match.tone} label={match.label} />
			{match.rest ? (
				<>
					{" "}
					<span className="ml-1">{match.rest}</span>
				</>
			) : null}
		</td>
	);
};
